import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Col, Dropdown, Input, Modal, Row, Space, Tabs, Tag } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, EyeOutlined, InboxOutlined, LinkOutlined, MailOutlined, MessageOutlined, PauseCircleOutlined, PlayCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { emptyAudience, type Audience } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { PageSkeleton, RecordError } from '../../components/states';
import { StatCard } from '../../components/StatCard';
import { errorMessage } from '../../api/client';
import { pctOf, useDeleteSequence, useDuplicateSequence, useSequence, useSequenceStatus, useUpdateSequence } from '../../features/sequences/api';
import { useAudiencePreview } from '../../features/campaigns/api';
import { AudienceEditor } from '../campaigns/AudienceEditor';
import { plural } from '../../utils/format';
import { SEQUENCE_STATUS_COLOR, sequenceStatusLabel } from './SequencesPage';
import { StepsTab } from './StepsTab';
import { PeopleTab } from './PeopleTab';
import { SettingsTab } from './SettingsTab';

const TABS = ['steps', 'people', 'audience', 'settings'] as const;

function AudienceTab({ sequenceId, saved, verified, active }: { sequenceId: string; saved: Audience; verified: { requireVerified: boolean; includeUnconfirmed: boolean }; active: boolean }) {
  const { message } = App.useApp();
  const update = useUpdateSequence();
  const [audience, setAudience] = useState<Audience>(() => ({ ...emptyAudience(), ...saved, include: { ...emptyAudience().include, ...saved.include } }));
  const preview = useAudiencePreview(audience, verified);
  const dirty = JSON.stringify(audience) !== JSON.stringify({ ...emptyAudience(), ...saved, include: { ...emptyAudience().include, ...saved.include } });
  const save = () =>
    update
      .mutateAsync({ id: sequenceId, audience })
      .then(() => message.success(active ? 'Audience saved. Use “Add audience” on the People tab to add newcomers now.' : 'Audience saved'))
      .catch((e) => message.error(errorMessage(e)));
  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        title="Pick the tags, lists or segments to reach. Everyone who can be emailed joins when you activate the sequence; switch on “Add new people automatically” in Settings to keep adding newcomers."
        action={<Button type="primary" size="small" disabled={!dirty} loading={update.isPending} onClick={save}>Save audience</Button>}
      />
      <AudienceEditor
        value={audience}
        onChange={setAudience}
        preview={preview.data}
        previewLoading={preview.isFetching}
        previewError={preview.error}
        verified={verified}
        onVerified={() => message.info('Change who can be emailed under Settings → Tracking & deliverability')}
      />
    </Space>
  );
}

export function SequencePage() {
  const { id } = useParams<{ id: string }>();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const sequence = useSequence(id);
  const status = useSequenceStatus();
  const update = useUpdateSequence();
  const duplicate = useDuplicateSequence();
  const remove = useDeleteSequence();
  const [renaming, setRenaming] = useState<string | null>(null);
  const tab = (TABS as readonly string[]).includes(sp.get('tab') ?? '') ? (sp.get('tab') as (typeof TABS)[number]) : 'steps';

  if (sequence.isError) return <RecordError error={sequence.error} what="sequence" backTo="/cold-email" backLabel="Back to Cold Emailing" onRetry={() => void sequence.refetch()} />;
  if (!sequence.data) return <PageSkeleton />;
  const s = sequence.data;
  const st = s.stats;
  const running = s.status === 'ACTIVE';

  const setStatus = (action: 'activate' | 'pause' | 'archive') =>
    status
      .mutateAsync({ id: s.id, action })
      .then((r) => {
        if (action === 'activate') message.success(r.enrolled?.added ? `Running. ${plural(r.enrolled.added, 'person', 'people')} added from the audience.` : 'Running. Emails go out within the sending window.');
        else message.success(action === 'pause' ? 'Paused. Nobody gets emails until you resume.' : 'Archived');
      })
      .catch((e) => message.error(errorMessage(e)));

  const activate = () => {
    const missing = !s.agents.length ? 'Pick at least one agent under Settings.' : null;
    if (missing) return message.warning(missing);
    modal.confirm({
      title: s.status === 'DRAFT' ? `Activate "${s.name}"?` : `Resume "${s.name}"?`,
      content: `Everyone in the audience who can be emailed is added, and emails start going out ${s.settings.sendDays.length ? `within the sending window (${s.settings.sendFrom}–${s.settings.sendTo})` : '(no sending days picked!)'}: at most ${s.settings.newPerDay} new people and ${s.settings.maxPerDay} emails a day.`,
      okText: s.status === 'DRAFT' ? 'Activate' : 'Resume',
      onOk: () => setStatus('activate'),
    });
  };

  const rename = () => {
    if (!renaming?.trim()) return;
    update.mutateAsync({ id: s.id, name: renaming.trim() }).then(() => setRenaming(null)).catch((e) => message.error(errorMessage(e)));
  };

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Cold Emailing', to: '/cold-email' }, { title: s.name }]}
        title={
          <Space size={10} wrap>
            {s.name}
            <Tag color={SEQUENCE_STATUS_COLOR[s.status]}>{sequenceStatusLabel(s.status)}</Tag>
          </Space>
        }
        docTitle={s.name}
        extra={
          <Space wrap>
            {running ? (
              <Button icon={<PauseCircleOutlined />} loading={status.isPending} onClick={() => setStatus('pause')}>Pause</Button>
            ) : (
              <Button type="primary" icon={<PlayCircleOutlined />} loading={status.isPending} onClick={activate}>{s.status === 'DRAFT' ? 'Activate' : 'Resume'}</Button>
            )}
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  { key: 'rename', icon: <EditOutlined />, label: 'Rename' },
                  { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate' },
                  ...(s.status !== 'ARCHIVED' ? [{ key: 'archive', icon: <InboxOutlined />, label: 'Archive' }] : []),
                  { type: 'divider' as const },
                  { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
                ],
                onClick: ({ key }) => {
                  if (key === 'rename') setRenaming(s.name);
                  if (key === 'archive') setStatus('archive');
                  if (key === 'duplicate') duplicate.mutateAsync(s.id).then((d) => navigate(`/cold-email/${d.id}`)).catch((e) => message.error(errorMessage(e)));
                  if (key === 'delete')
                    modal.confirm({
                      title: `Delete sequence "${s.name}"?`,
                      content: 'Everyone still in it stops getting emails. Emails already sent and their replies stay in the Inbox.',
                      okText: 'Delete',
                      okButtonProps: { danger: true },
                      onOk: () => remove.mutateAsync(s.id).then(() => navigate('/cold-email')).catch((e) => message.error(errorMessage(e))),
                    });
                },
              }}
            >
              <Button icon={<EllipsisOutlined />} aria-label="More actions" />
            </Dropdown>
          </Space>
        }
      />

      {!s.agents.length && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title="No agents send for this sequence yet." action={<Button size="small" onClick={() => setSp({ tab: 'settings' })}>Pick agents</Button>} />}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8} xl={4}><StatCard icon={<TeamOutlined />} label="People" value={st.enrolled} note={`${st.active} active · ${st.contacted} contacted`} /></Col>
        <Col xs={12} md={8} xl={4}><StatCard icon={<MailOutlined />} label="Emails sent" value={st.sent} note={`${st.sentToday} today · ${st.newToday} new`} /></Col>
        <Col xs={12} md={8} xl={4}><StatCard icon={<EyeOutlined />} label="Opened" value={pctOf(st.opened, st.sent)} note={plural(st.opened, 'email')} /></Col>
        <Col xs={12} md={8} xl={4}><StatCard icon={<LinkOutlined />} label="Clicked" value={pctOf(st.clicked, st.sent)} note={plural(st.clicked, 'email')} /></Col>
        <Col xs={12} md={8} xl={4}><StatCard icon={<MessageOutlined />} label="Replied" value={pctOf(st.replied, st.contacted)} note={plural(st.replied, 'person', 'people')} /></Col>
        <Col xs={12} md={8} xl={4}><StatCard icon={<InboxOutlined />} label="Bounced / unsub." value={`${st.bounced} / ${st.unsubscribed}`} note={`${st.completed} finished`} /></Col>
      </Row>

      <Tabs
        activeKey={tab}
        onChange={(k) => setSp({ tab: k })}
        destroyOnHidden
        items={[
          { key: 'steps', label: `Steps (${s.steps.length})`, children: <StepsTab sequence={s} /> },
          { key: 'people', label: `People (${st.enrolled})`, children: <PeopleTab sequence={s} /> },
          { key: 'audience', label: 'Audience', children: <AudienceTab sequenceId={s.id} saved={s.audience} verified={{ requireVerified: s.settings.requireVerified, includeUnconfirmed: s.settings.includeUnconfirmed }} active={running} /> },
          { key: 'settings', label: 'Settings', children: <SettingsTab sequence={s} /> },
        ]}
      />

      <Modal title="Rename sequence" open={renaming !== null} onCancel={() => setRenaming(null)} okText="Rename" okButtonProps={{ disabled: !renaming?.trim() }} confirmLoading={update.isPending} onOk={rename} destroyOnHidden>
        <Input aria-label="Sequence name" autoFocus maxLength={160} value={renaming ?? ''} onChange={(e) => setRenaming(e.target.value)} onPressEnter={rename} />
      </Modal>
    </div>
  );
}
